// Static onboarding step content. Completion (`done`) and auto-generation
// progress (`autoGeneration`) are injected at runtime by OnboardingContext —
// this file is pure presentational data. `icon` is the
// per-step lucide glyph; the checklist renders it in a neutral (muted) tile so
// the row stays minimal — the only color cue is the green check on completion.
import { Bot, FlaskConical, BarChart3, Zap, PhoneCall } from "lucide-react";

export const ONBOARDING_STEPS = [
  {
    id: "create-agent",
    title: "Create your first agent",
    estTime: "2 min",
    icon: Bot,
    description: "Tell us about the AI agent you want to test.",
    substeps: [
      {
        title: "Name your agent",
        body: "Give it a recognizable name so you can find it later.",
        media: null,
      },
      {
        title: "Pick a provider",
        body: "Choose the platform your agent runs on — Vapi, Retell, or Pipecat.",
        media: null,
      },
      {
        title: "Add its phone number or API endpoint",
        body: "This is how we reach your agent to run test calls.",
        media: null,
      },
    ],
    cta: { label: "Create Agent", href: "/ai-testing-agent/create" },
  },
  {
    id: "add-scenarios",
    title: "Add test scenarios",
    estTime: "3 min",
    icon: FlaskConical,
    description: "Scenarios are the situations we'll put your agent through.",
    substeps: [
      {
        title: "Generate with AI",
        body: "Describe your use case and we'll draft realistic test scenarios — refund disputes, transfers, edge cases.",
        media: null,
      },
      {
        title: "Or write your own",
        body: "Add scenarios manually if you already know the conversations you want to cover.",
        media: null,
      },
    ],
    cta: { label: "Generate Scenarios", href: "/live-simulations/generate" },
    secondaryCta: { label: "Write manually", href: "/live-simulations/scenario" },
    // The first agent gets scenarios auto-generated; the checklist shows their
    // progress (`autoGeneration`, injected by OnboardingContext) and links here.
    autoGenerate: { key: "scenarios", noun: "scenarios", viewHref: "/live-simulations/scenario" },
  },
  {
    id: "define-metrics",
    title: "Define your evaluation metrics",
    estTime: "3 min",
    icon: BarChart3,
    description: "Metrics decide what “good” looks like when we score every call.",
    substeps: [
      {
        title: "Start with the built-in metrics",
        body: "You already have predefined audio and conversation metrics — latency, interruptions, silence, sentiment, goal completion and more — enabled out of the box.",
        media: null,
      },
      {
        title: "Add your own",
        body: "Create custom metrics for what's specific to your agent — script adherence, compliance phrases, tone — described in plain language.",
        media: null,
      },
      {
        title: "Or generate them with AI",
        body: "Describe what you care about and we'll draft tailored metrics for you to tweak and save.",
        media: null,
      },
    ],
    cta: { label: "Set up metrics", href: "/evaluation/metrics" },
    secondaryCta: { label: "Generate with AI", href: "/evaluation/generate-metrics" },
    autoGenerate: { key: "metrics", noun: "metrics", viewHref: "/evaluation/metrics/custom" },
  },
  {
    id: "run-batch",
    title: "Run your first batch test",
    estTime: "5 min",
    icon: Zap,
    description: "Our AI caller runs every scenario against your agent and scores the results.",
    substeps: [
      {
        title: "Select your scenarios",
        body: "Pick which scenarios to include in the batch.",
        media: null,
      },
      {
        title: "Launch the run",
        body: "We place the calls and evaluate each transcript against your metrics.",
        media: null,
      },
      {
        title: "Review pass / fail",
        body: "See exactly where your agent broke, with transcripts and metric breakdowns.",
        media: null,
      },
    ],
    cta: { label: "Run Batch Test", href: "/batch-run/new" },
  },
  {
    id: "connect-calls",
    title: "Connect calls for observability",
    estTime: "5 min",
    icon: PhoneCall,
    description: "Send your real production calls so we can transcribe, evaluate, and flag issues automatically.",
    substeps: [
      {
        title: "Grab your API key",
        body: "Create an API key in Settings to authenticate your requests.",
        media: null,
      },
      {
        title: "Send a call for analysis",
        body: "POST your call (recording or transcript) to the ingest endpoint — every call gets analyzed automatically.",
        media: {
          type: "code",
          lang: "bash",
          content:
            'curl -X POST https://api.rubrichq.io/api/public/v1/calls \\\n' +
            '  -H "Authorization: Bearer $RUBRIC_API_KEY" \\\n' +
            '  -H "Content-Type: application/json" \\\n' +
            "  -d '{\n" +
            '    "call_identifier": "call-abc-123",\n' +
            '    "agent_id": 42,\n' +
            '    "recording_url": "https://.../call.wav"\n' +
            "  }'",
        },
      },
    ],
    cta: { label: "Connect via API", href: "/settings/api_keys" },
    secondaryCta: { label: "Upload calls", href: "/observability/conversations" },
  },
];
